import JsBarcode from 'jsbarcode';

/**
 * Generates a crisp barcode image as a base64 data URL, optimised for
 * @react-pdf/renderer usage (barcodeImage: { width: 300, height: 190 }).
 *
 * Strategy for sharpness:
 *  - JsBarcode draws at the EXACT physical pixel size (barWidth * scale,
 *    barHeight * scale) — no upscaling / stretching ever happens.
 *  - The composite canvas is also scale× so text is equally crisp.
 *  - imageSmoothingEnabled = false everywhere.
 *  - The PNG exported is high-DPI; react-pdf will downscale it cleanly.
 *
 * Layout  (logical px, scale=4 → physical px = logical × 4):
 *   ┌─────────────────────────────┐  ← margin
 *   │      10853446870 0          │  ← number (fontSize 18)
 *   │  ▌▌▌▌▌ ▌▌▌▌ ▌▌▌▌▌▌▌▌▌▌   │  ← barcode bars (height 70)
 *   └─────────────────────────────┘  ← margin
 *
 */
export function generateBarcodeBase64(
  proNumber: string | number | null | undefined,
  options?: {
    format?:          string;   // default 'CODE128'
    barWidth?:        number;   // logical px per bar module  (default 2)
    barHeight?:       number;   // logical px bar height      (default 70)
    imageWidth?:      number;   // logical canvas width       (default 280)
    margin?:          number;   // outer margin               (default 8)
    numberGap?:       number;   // gap: number → bar top      (default 2)
    numberFontSize?:  number;   // font size                  (default 18)
    backgroundColor?: string;   // default '#ffffff'
    lineColor?:       string;   // default '#000000'
    scale?:           number;   // DPI multiplier             (default 4)
  }
): string | null {
  const value = proNumber?.toString().trim();
  if (!value) return null;

  try {
    /* ── resolved config ───────────────────────────────────────────────── */
    const imageWidth  = options?.imageWidth      ?? 280;
    const barHeight   = options?.barHeight        ?? 70;
    const barWidth    = options?.barWidth         ?? 2;
    const margin      = options?.margin           ?? 8;
    const numberGap   = options?.numberGap        ?? 2;
    const bgColor     = options?.backgroundColor ?? '#ffffff';
    const lineColor   = options?.lineColor        ?? '#000000';
    const fontSize    = options?.numberFontSize   ?? 18;
    const scale       = options?.scale            ?? 4;   // 4× → very crisp in PDF

    /* ── 1. JsBarcode → temp canvas at EXACT physical size ────────────── */
    const barcodeCanvas = document.createElement('canvas');
    // width/height here are physical pixels — JsBarcode fills them exactly.
    JsBarcode(barcodeCanvas, value, {
      format:       options?.format ?? 'CODE128',
      width:        barWidth  * scale,   // physical px per module
      height:       barHeight * scale,   // physical px bar height
      displayValue: false,
      margin:       0,
      background:   bgColor,
      lineColor:    lineColor,
    });

    /* ── 2. Layout math (all in logical px) ────────────────────────────── */
    const numberHeight  = fontSize + 2;   // tight line-height
    const logicalH      = margin + numberHeight + numberGap + barHeight + margin;

    /* ── 3. High-res composite canvas ─────────────────────────────────── */
    const out   = document.createElement('canvas');
    out.width   = imageWidth * scale;
    out.height  = logicalH  * scale;

    const ctx   = out.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;   // ← no interpolation anywhere

    /* ── 4. Background ─────────────────────────────────────────────────── */
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, out.width, out.height);

    /* ── 5. Number — drawn in logical-px space via ctx.scale ───────────── */
    ctx.save();
    ctx.scale(scale, scale);
    ctx.font         = `bold ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle    = lineColor;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(value, imageWidth / 2, margin);
    ctx.restore();

    /* ── 6. Barcode — copied 1-to-1 in physical-px space ──────────────── */
    //   barcodeCanvas.width may be slightly narrower or wider than out.width
    //   depending on how many bar modules CODE128 needs; we centre it.
    const barcodePhysX = Math.round((out.width - barcodeCanvas.width) / 2);
    const barcodePhysY = (margin + numberHeight + numberGap) * scale;

    ctx.drawImage(barcodeCanvas, barcodePhysX, barcodePhysY);
    // No width/height args → 1:1 copy, zero scaling, zero blur.

    return out.toDataURL('image/png');
  } catch (err) {
    console.error('generateBarcodeBase64 error:', err);
    return null;
  }
}
